import { getContainerCharacterization } from "../../../../services/container";
import { downloadFile } from "../../../../shared/utils/utils";
import { getSettingsData } from "../../../../services/settings";
import {
  getEffectiveThresholdCm,
  getRegulationTypeFromContainer,
  REGULATION_TYPES,
} from "../../characterizationDetails/constants/regulation";
import { DECREE_1199_THRESHOLD_CM } from "./buildCharacterizationCsvFiles";
import { serializeCsvContent } from "./buildCharacterizationCsvFiles";
import { resolvePeriodMaterialRange } from "./characterizationViewMode";
import { formatProducerNameWithUniqueId } from "../../characterizationDetails/utils/clientUtils";

const fetchCharacterizationDataForCsv = async (
  period,
  container,
  superContainer,
  i18n,
  characterizationType,
  sizeThreshold,
  useLatestCoefficients = false,
) => {
  const containerId = superContainer ? superContainer._id : container?._id;
  if (!period || !containerId) {
    return null;
  }

  const { periodMaterialStart, periodMaterialStop } =
    resolvePeriodMaterialRange(period);
  if (!periodMaterialStart || !periodMaterialStop) {
    return null;
  }

  try {
    const requests = getContainerCharacterization(
      {
        periodMaterialStart,
        periodMaterialStop,
        customDate: true,
        language: i18n.language,
        characterizationType,
        sizeThreshold: getEffectiveThresholdCm(
          characterizationType,
          sizeThreshold,
        ),
        useLatestCoefficients,
      },
      containerId,
    );
    const requestArray = Array.isArray(requests) ? requests : [requests];
    const responses = await Promise.all(requestArray);
    const validResponses = responses.filter((r) => r?.status === 200);
    if (validResponses.length === 0) {
      return null;
    }
    const responseData = validResponses[0].data;

    return {
      materialDelivery: responseData.materialDelivery || [],
      parentCategoriesList: responseData.parentCategoriesList || [],
      totalWasteMass: responseData.totalWasteMass || 0,
      totalWasteVolume: responseData.totalWasteVolume || 0,
      totalMassRecoverable: responseData.totalMassRecoverable || 0,
      totalVolumeRecoverable: responseData.totalVolumeRecoverable || 0,
      materialTotal: responseData.materialTotal || 0,
      totalFinesMass: responseData.totalFinesMass || 0,
      totalFinesVolume: responseData.totalFinesVolume || 0,
      finesBelowThreshold: responseData.finesBelowThreshold || {
        count: 0,
        volumeObject: 0,
        massObject: 0,
      },
    };
  } catch (err) {
    console.error(
      "Erreur lors du chargement des données de caractérisation:",
      err,
    );
    return null;
  }
};

export const resolveClientThresholdCm = async () => {
  let sizeThreshold = 40;
  try {
    const settingsResponse = await getSettingsData();
    if (settingsResponse.data?.settings?.finesSizeThreshold !== undefined) {
      sizeThreshold = settingsResponse.data.settings.finesSizeThreshold;
    }
  } catch (error) {
    console.error("Error loading settings:", error);
  }
  return typeof sizeThreshold === "number" ? sizeThreshold : 40;
};

/**
 * Loads CSV datasets according to the container regulation (OMR zone → RHW, else classic + 1199 recap).
 */
export const fetchCsvExportDatasets = async ({
  period,
  container,
  superContainer,
  i18n,
  useLatestCoefficients = false,
}) => {
  const regulationType = getRegulationTypeFromContainer(container);

  if (regulationType === REGULATION_TYPES.RHW) {
    const regulationData = await fetchCharacterizationDataForCsv(
      period,
      container,
      superContainer,
      i18n,
      REGULATION_TYPES.RHW,
      0,
      useLatestCoefficients,
    );
    return {
      regulationType,
      clientThresholdCm: 0,
      clientThresholdData: regulationData,
      decreeData: null,
    };
  }

  const clientThresholdCm = await resolveClientThresholdCm();
  const [clientThresholdData, decreeData] = await Promise.all([
    fetchCharacterizationDataForCsv(
      period,
      container,
      superContainer,
      i18n,
      REGULATION_TYPES.CLASSIC,
      clientThresholdCm,
      useLatestCoefficients,
    ),
    fetchCharacterizationDataForCsv(
      period,
      container,
      superContainer,
      i18n,
      REGULATION_TYPES.DECREE_1199,
      DECREE_1199_THRESHOLD_CM,
      useLatestCoefficients,
    ),
  ]);

  return {
    regulationType,
    clientThresholdCm,
    clientThresholdData,
    decreeData,
  };
};

export const getMaterialCatalogFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem("materialsDescription") || "[]");
  } catch {
    return [];
  }
};

export const downloadCsvFiles = async (csvFiles) => {
  for (const { data: csvData, filename: csvFilename } of csvFiles) {
    const blob = new Blob([serializeCsvContent(csvData)], {
      type: "text/csv;charset=utf-8;",
    });
    await downloadFile(blob, csvFilename);
  }
};

export const isPeriodExportable = (period) => {
  const client = period?.clientParsed ?? null;
  let clientName = client?.name;
  if (!clientName && period?.client) {
    try {
      clientName = JSON.parse(period.client)?.name;
    } catch {
      clientName = null;
    }
  }
  const wasteName =
    period?.wasteCategory?.wasteCategoryName ??
    (() => {
      try {
        return period?.waste_category
          ? JSON.parse(period.waste_category)?.wasteCategoryName
          : null;
      } catch {
        return null;
      }
    })();
  const weightNum = period?.weight != null ? Number(period.weight) : NaN;

  return (
    Boolean(clientName && String(clientName).trim() !== "-") &&
    !Number.isNaN(weightNum) &&
    weightNum > 0 &&
    Boolean(wasteName && String(wasteName).trim() !== "-")
  );
};

export const formatCsvReportDate = (dateInput) => {
  const date =
    dateInput instanceof Date ? dateInput : new Date(dateInput || "");
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}_${month}_${day}`;
};

export const getPeriodProducerName = (period) => {
  if (period?.clientParsed?.name) {
    return formatProducerNameWithUniqueId(period.clientParsed);
  }
  if (!period?.client) return "-";
  try {
    const parsed =
      typeof period.client === "string"
        ? JSON.parse(period.client)
        : period.client;
    return formatProducerNameWithUniqueId(parsed);
  } catch {
    return "-";
  }
};
